import { useCallback, useEffect, useRef } from 'react';

import { type EmbeddedPaymentElementResult } from '@stripe/stripe-react-native';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { type PaymentMethod } from '../../types';
import { fallbackNoPaymentIsNeeded, fallbackToSavedPaymentMethod, StripeLogger } from '../../utils';
import { useNotifyStripeCancel } from '../useNotifyStripeCancel';
import { useNotifyStripeError } from '../useNotifyStripeError';

/**
 * Order submission includes playback, confirmation and submission.
 * Confirmation -> Creating and confirming payment intent OR creating payment method.
 * Playback -> Showing order playback (customer can go back).
 * Submission -> Calling the submission mutation to the backend.
 * When {isPaymentStatePollingEnabled}, it follows this flow: Playback -> Confirmation -> Submission
 * When it is disabled, it follows this flow: Confirmation -> Playback -> Submission
 */
export const useStripeIntegration = (props: UseStripeIntegrationProps) => {
  const {
    paymentMethods,
    total,
    selectedPaymentMethod,
    confirmStripe,
    startOrderPolling,
    requestOrderSubmission,
    cancelRequest,
    startStripeConfirmation,
    cancelPreviousIntent,
    flagFailureAsShown,
    failPayment,
  } = props;

  const isPaymentStatePollingEnabled = useFeatureFlag('CELS-4828-order-payment-state-polling-enabled'); // prettier-ignore

  // ─── Telemetry And Notifications ───────────────────────────────────────────

  const trackStripeUsage = useStripeUsageTracking();
  const notifyStripeError = useNotifyStripeError(trackStripeUsage);
  const notifyStripeCancel = useNotifyStripeCancel();

  // ─── Payment Confirmation ─────────────────────────────────────────────────

  const stableRefs = useStableRefs({
    isPaymentStatePollingEnabled,
    cancelRequest,
    cancelPreviousIntent,
    startStripeConfirmation,
    requestOrderSubmission,
    startOrderPolling,
    selectedPaymentMethod,
    paymentMethods,
    total,
    confirmStripe,
    trackStripeUsage,
    notifyStripeError,
    notifyStripeCancel,
    flagFailureAsShown,
    failPayment,
  });

  const confirmPayment = useCallback(async () => {
    const {
      isPaymentStatePollingEnabled,
      selectedPaymentMethod,
      paymentMethods,
      total,
      startStripeConfirmation,
      cancelRequest,
      cancelPreviousIntent,
      requestOrderSubmission,
      startOrderPolling,
      confirmStripe,
      trackStripeUsage,
      notifyStripeError,
      notifyStripeCancel,
      flagFailureAsShown,
      failPayment,
    } = stableRefs.current;

    startStripeConfirmation();

    // Use first saved payment method if no payment method is selected from the SDK.
    if (!selectedPaymentMethod && paymentMethods.length > 0) {
      trackStripeUsage({ event: 'confirming_with_fallback' });
      requestOrderSubmission(fallbackToSavedPaymentMethod(paymentMethods)?.id);

      return;
    }

    // Use first saved payment method or fallback to none if the total is zero.
    if (total === 0) {
      trackStripeUsage({ event: 'confirming_with_zero_total' });
      requestOrderSubmission(fallbackNoPaymentIsNeeded(paymentMethods)?.id);

      return;
    }

    const result = (await confirmStripe()) as EmbeddedPaymentElementResult & {
      paymentMethodId: string;
    };

    StripeLogger.logConfirmResult(result);

    // Start polling the order payment state when the payment intent is confirmed.
    if (result.status === 'completed' && isPaymentStatePollingEnabled) {
      trackStripeUsage({ event: 'result_completed', result });
      startOrderPolling(result.paymentMethodId);
      return;
    }

    // Start order submission when the payment method is available.
    if (result.status === 'completed' && !isPaymentStatePollingEnabled) {
      trackStripeUsage({ event: 'result_completed', result });
      requestOrderSubmission(result.paymentMethodId);
      return;
    }

    if (result.status === 'failed') {
      StripeLogger.logConfirmError(result.error);
      trackStripeUsage({ event: 'result_failed', result });
      notifyStripeError(result.error.message ?? result.error, { canShowErrorMessage: true });
      cancelRequest(true);
      failPayment();
      flagFailureAsShown();
      return;
    }

    // If the payment intent is flagged as canceled, also call the cancel mutation to cancel the payment state.
    if (result.status === 'canceled') {
      StripeLogger.logCancelation();
      cancelPreviousIntent();
      trackStripeUsage({ event: 'result_canceled', result });
      notifyStripeCancel();
      cancelRequest(true);
      failPayment();
      flagFailureAsShown();
    }
  }, [stableRefs]);

  return { confirmPayment };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * To avoid re-rendering the Stripe SDK by changing the callback that it receives.
 */
const useStableRefs = (props: UseStableRefsProps) => {
  const {
    isPaymentStatePollingEnabled,
    cancelRequest,
    cancelPreviousIntent,
    startStripeConfirmation,
    requestOrderSubmission,
    startOrderPolling,
    selectedPaymentMethod,
    paymentMethods,
    total,
    confirmStripe,
    trackStripeUsage,
    notifyStripeError,
    notifyStripeCancel,
    flagFailureAsShown,
    failPayment,
  } = props;

  const stableRefs = useRef({
    isPaymentStatePollingEnabled,
    cancelRequest,
    cancelPreviousIntent,
    startStripeConfirmation,
    requestOrderSubmission,
    startOrderPolling,
    selectedPaymentMethod,
    paymentMethods,
    total,
    confirmStripe,
    trackStripeUsage,
    notifyStripeError,
    notifyStripeCancel,
    flagFailureAsShown,
    failPayment,
  });

  useEffect(() => {
    stableRefs.current = {
      isPaymentStatePollingEnabled,
      cancelRequest,
      cancelPreviousIntent,
      startStripeConfirmation,
      requestOrderSubmission,
      startOrderPolling,
      selectedPaymentMethod,
      paymentMethods,
      total,
      confirmStripe,
      trackStripeUsage,
      notifyStripeError,
      notifyStripeCancel,
      flagFailureAsShown,
      failPayment,
    };
  }, [
    isPaymentStatePollingEnabled,
    selectedPaymentMethod,
    paymentMethods,
    total,
    startStripeConfirmation,
    cancelRequest,
    cancelPreviousIntent,
    requestOrderSubmission,
    startOrderPolling,
    confirmStripe,
    trackStripeUsage,
    notifyStripeError,
    notifyStripeCancel,
    flagFailureAsShown,
    failPayment,
  ]);

  return stableRefs;
};

export const useStripeUsageTracking = () => {
  const { track } = useTelemetry();

  const trackStripeUsage = useCallback(
    (payload: Record<string, unknown>) => {
      track('checkout.stripe-usage', payload);
    },
    [track],
  );

  return trackStripeUsage;
};

// ─── Types ──────────────────────────────────────────────────────────────────

type UseStripeIntegrationProps = {
  paymentMethods: readonly PaymentMethod[];
  total: number;
  selectedPaymentMethod: string | undefined;
  confirmStripe: () => Promise<EmbeddedPaymentElementResult>;
  startOrderPolling: (paymentIntentOrMethodId?: string) => void;
  requestOrderSubmission: (paymentIntentOrMethodId?: string) => void;
  cancelRequest: (shouldCancel: boolean) => void;
  cancelPreviousIntent: () => void;
  startStripeConfirmation: () => void;
  flagFailureAsShown: () => void;
  failPayment: () => void;
};

type UseStableRefsProps = UseStripeIntegrationProps & {
  isPaymentStatePollingEnabled: boolean;
  trackStripeUsage: (payload: Record<string, unknown>) => void;
  notifyStripeError: (error: string, options: { canShowErrorMessage?: boolean }) => void;
  notifyStripeCancel: () => void;
};
